/**
 * Cookie consent state — the single source of truth for the banner and for
 * gtag.ts's decision on whether analytics is allowed to run.
 *
 * frontend/src/views/legal/Cookies.vue already promises visitors "Accept All /
 * Essential Only / Customize" on first visit. No banner existed to deliver
 * that, so GA cookies were being set with no consent mechanism at all — this
 * makes the published policy true rather than rewriting it to be less
 * protective.
 *
 * Only one non-essential category exists in code today (Analytics/GA), so
 * "Customize" exposes exactly one real toggle. Categories the policy mentions
 * but that aren't implemented (ads, affiliate tracking) are not offered as
 * toggles — a toggle that doesn't control anything real would be worse than
 * not having one.
 */
import { ref, computed } from 'vue'

export interface ConsentState {
  necessary: true // always on; cannot be disabled
  analytics: boolean
}

const STORAGE_KEY = 'takefav_cookie_consent'
const CONSENT_VERSION = 1 // bump to re-prompt everyone if categories change

interface StoredConsent {
  version: number
  analytics: boolean
  decidedAt: string
}

function readStoredConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredConsent
    if (parsed.version !== CONSENT_VERSION) return null
    return parsed
  } catch {
    // Corrupt value or localStorage unavailable (private browsing, disabled
    // storage) — treat as "no decision yet" rather than throwing.
    return null
  }
}

const stored = readStoredConsent()

/** null = no decision recorded yet; show the banner. */
const analyticsAllowed = ref<boolean | null>(stored ? stored.analytics : null)

export function useCookieConsent() {
  const hasDecided = computed(() => analyticsAllowed.value !== null)
  const analyticsEnabled = computed(() => analyticsAllowed.value === true)

  function persist(analytics: boolean) {
    analyticsAllowed.value = analytics
    try {
      const payload: StoredConsent = {
        version: CONSENT_VERSION,
        analytics,
        decidedAt: new Date().toISOString(),
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    } catch {
      // If storage isn't available the choice just won't persist across
      // reloads; the in-memory ref above still governs this page view.
    }
  }

  const acceptAll = () => persist(true)
  const acceptNecessaryOnly = () => persist(false)
  /** Used by the "Customize" panel, which currently has one real toggle. */
  const acceptCustom = (analytics: boolean) => persist(analytics)

  return {
    hasDecided,
    analyticsEnabled,
    acceptAll,
    acceptNecessaryOnly,
    acceptCustom,
  }
}
